import React, { useState, useEffect } from "react";
import { useLanguage } from "./LanguageProvider"; // LanguageProviderからuseLanguageをインポート

// 再利用可能なフォームフィールドコンポーネント
const FormField = ({
  label,
  type,
  name,
  value,
  onChange,
  options,
  checked,
  onCheckboxChange,
  rows,
  min,
  disabled,
  placeholder,
  required,
}) => {
  if (type === "textarea") {
    return (
      <div>
        {label}
        <label>
          <textarea
            name={name}
            value={value}
            onChange={onChange}
            rows={rows}
            disabled={disabled}
            placeholder={placeholder}
          />
        </label>
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <div>
        <label>
          <input
            type="checkbox"
            name={name}
            checked={checked}
            onChange={onCheckboxChange}
            disabled={disabled}
          />
          {label}
        </label>
      </div>
    );
  }

  if (type === "radio") {
    return (
      <div>
        <label>
          <input
            type="radio"
            name={name}
            value={value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
          {label}
        </label>
      </div>
    );
  }

  if (type === "select") {
    return (
      <div>
        <label>{label}</label>
        <select
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
        >
          {options.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (type === "date") {
    return (
      <div>
        <label>{label}</label>
        <input
          type="date"
          name={name}
          value={value}
          onChange={onChange}
          min={min}
          disabled={disabled}
        />
      </div>
    );
  }

  if (type === "text" && required) {
    return (
      <div>
        <label>{label}</label>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div>
      <label>{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
};

const ContactForm = ({ language, setSubmit }) => {
  const { translate } = useLanguage(); // useLanguageフックでtranslateを取得

  const [isBusiness, setIsBusiness] = useState(false);
  const [additionalCharacters, setAdditionalCharacters] = useState(false);
  const [addBackground, setAddBackground] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [agreeToTOS, setAgreeToTOS] = useState(false);
  const [isInternational, setIsInternational] = useState(false);
  const [formData, setFormData] = useState({
    activityName: "",
    requestType: "",
    characterType: "normal",
    addTimes: 0,
    businessName: "",
    email: "",
    activityLink: "",
    characterCount: 1,
    backgroundDetails: "",
    emoteCount: 1,
    references: [],
    referencesDetail: [],
    details: "",
    contactMethod: "discord",
    deliveryDate: "",
    isInternational: isInternational,
    language: language,
  });

  useEffect(() => {
    formData.language = language;
  }, [language]); // 言語が変わるたびに再実行

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // すべてのフォーム要素に対応
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const renderCharacterType = () => (
    <FormField
      label={translate("form.characterType.label")}
      type="select"
      name="characterType"
      value={formData.characterType}
      onChange={handleChange}
      options={[
        { label: translate("form.characterType.normal"), value: "normal" },
        { label: translate("form.characterType.chibi"), value: "chibi" },
      ]}
      disabled={showModal}
    />
  );

  const renderBackgroundFields = () => (
    <>
      <FormField
        label={translate("form.backgroundFields.addBackground")}
        type="checkbox"
        name="addBackground"
        checked={addBackground}
        onCheckboxChange={() => setAddBackground(!addBackground)}
        disabled={showModal}
      />
      {addBackground && (
        <FormField
          label={translate("form.backgroundFields.backgroundDetails")}
          type="text"
          name="backgroundDetails"
          value={formData.backgroundDetails}
          onChange={handleChange}
          disabled={showModal}
        />
      )}
    </>
  );

  const renderTimes = () => (
    <>
      <FormField
        label={translate("form.times.label")}
        type="text"
        name="addTimes"
        value={formData.addTimes}
        onChange={handleChange}
        disabled={showModal}
      />
    </>
  );

  const renderEmoteFields = () => (
    <>
      <FormField
        label={translate("form.emoteFields.label")}
        type="number"
        name="emoteCount"
        value={formData.emoteCount}
        onChange={(e) => {
          // 入力値が0以下にならないように
          const value = Math.max(1, parseInt(e.target.value, 10));
          handleChange({
            target: { name: "emoteCount", value },
          });
        }}
        disabled={showModal}
      />
      {Array.from({ length: formData.emoteCount }).map((_, index) => (
        <FormField
          key={index}
          label={`${translate("form.emoteFields.emote")}${index + 1}`}
          type="text"
          name={`emote_${index}`}
          value={formData[`emote_${index}`] || ""}
          onChange={handleChange}
          disabled={showModal}
          placeholder={translate("form.emoteFields.placeholder")}
          required="true"
        />
      ))}
    </>
  );

  const renderDetails = () => (
    <FormField
      label={translate("form.details.label")}
      type="textarea"
      name="details"
      value={formData.details}
      onChange={handleChange}
      rows="10"
      disabled={showModal}
    />
  );

  const renderReferences = () => (
    <div>
      <div className="renderReferences">
        <label>{translate("form.references.label")}</label>
        <button
          className="plusButton"
          type="button"
          onClick={() => {
            const newReferences = [...(formData.references || []), ""];
            const newReferencesDetail = [
              ...(formData.referencesDetail || []),
              "",
            ]; // 空の文字列を追加

            setFormData({
              ...formData,
              references: newReferences,
              referencesDetail: newReferencesDetail, // 新しいreferencesDetailをセット
            });
          }}
          disabled={showModal}
        >
          ＋
        </button>
      </div>
      {formData.references?.map((reference, index) => (
        <div key={index}>
          <label>
            {translate("form.references.reference")}
            {index + 1}
          </label>
          <input
            type="text"
            name={`reference_${index}`}
            value={reference}
            onChange={(e) => {
              const newReferences = [...(formData.references || [])];
              newReferences[index] = e.target.value;
              setFormData({ ...formData, references: newReferences });
            }}
            disabled={showModal}
          />
          <FormField
            label={translate("form.references.referenceDetail")}
            type="text"
            name={`referenceDetail_${index}`}
            value={formData.referencesDetail[index] || ""}
            onChange={(e) => {
              const newReferencesDetail = [
                ...(formData.referencesDetail || []),
              ];
              newReferencesDetail[index] = e.target.value;
              setFormData({
                ...formData,
                referencesDetail: newReferencesDetail,
              });
            }}
            disabled={showModal}
          />
          <button
            className="minusButton"
            type="button"
            onClick={() => {
              const newReferences = [...(formData.references || [])];
              newReferences.splice(index, 1);

              const newReferencesDetail = [...formData.referencesDetail];
              newReferencesDetail.splice(index, 1);
              setFormData({
                ...formData,
                references: newReferences,
                referencesDetail: newReferencesDetail,
              });
            }}
            disabled={showModal}
          >
            －
          </button>
        </div>
      ))}
    </div>
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.activityName.trim()) {
      alert(translate("form.alerts.activityName"));
      return;
    }

    if (!formData.email.trim()) {
      alert(translate("form.alerts.email"));
      return;
    }

    if (isBusiness && !formData.businessName.trim()) {
      alert(translate("form.alerts.businessName"));
      return;
    }

    if (!formData.details.trim()) {
      alert(translate("form.alerts.details"));
      return;
    }

    if (!formData.requestType.trim()) {
      alert(translate("form.alerts.requestType"));
      return;
    }

    if (!formData.deliveryDate.trim()) {
      alert(translate("form.alerts.deliveryDate"));
      return;
    }
    if (agreeToTOS === false) {
      alert(translate("form.alerts.agreeToTOS"));
      return;
    }

    const { total, details } = calculateEstimate();

    setFormData({
      ...formData,
      estimatedCost: total,
      estimatedDetails: details,
    });

    setShowModal(true);
    setSubmit(true);
  };

  //valueと名詞の変換
  const typeMap = {
    animation: translate("typeMap.animation"),
    illustration: translate("typeMap.illustration"),
    animationEmote: translate("typeMap.animationEmote"),
    illustrationEmote: translate("typeMap.illustrationEmote"),
    chibi: translate("typeMap.chibi"),
    normal: translate("typeMap.normal"),
    others: translate("typeMap.others"),
  };

  const calculatePaypalFee = (amount) => {
    const domesticFeeRate = 0.036; // 国内手数料 3.6%
    const internationalFeeRate = 0.041; // 海外手数料 4.1%
    const exchangeFeeRate = 0.025; // 為替手数料 2.5%

    if (isInternational) {
      // 海外取引の手数料
      const paypalFee = amount * internationalFeeRate;
      const exchangeFee = amount * exchangeFeeRate; // 為替手数料
      return [paypalFee, exchangeFee];
    } else {
      // 国内取引の手数料
      const paypalFee = amount * domesticFeeRate;
      return paypalFee;
    }
  };

  //円➡ドル換算
  const convertCurrency = (amount) => {
    const exchangeRate = 100; // 例: 1ドル = 150円
    return language === "en" ? amount / exchangeRate : amount;
  };

  //自動見積もり
  const calculateEstimate = () => {
    let basePrice = 0;
    let total = 0;
    let details = [];
    switch (formData.requestType) {
      case "animation":
        basePrice = convertCurrency(
          formData.characterType === "chibi" ? 30000 : 50000
        );
        details.push(
          `${translate("paypal.animation.basePrice")}: ${translate(
            "paypal.moneyMark"
          )}${basePrice.toLocaleString()} ${translate("paypal.money")}`
        );
        total += basePrice;

        break;

      case "illustration":
        basePrice = convertCurrency(
          formData.characterType === "chibi" ? 15000 : 20000
        );

        details.push(
          `${translate("paypal.illustration.basePrice")}: ${translate(
            "paypal.moneyMark"
          )}${basePrice.toLocaleString()} ${translate("paypal.money")}`
        );
        total += basePrice;

        if (addBackground) {
          let bgPrice = convertCurrency(10000);
          details.push(
            `${translate("form.backgroundFields.addBackground")}: ${translate(
              "paypal.moneyMark"
            )}${bgPrice.toLocaleString()} ${translate("paypal.money")}`
          );
          total += bgPrice;
        }

        break;

      case "animationEmote":
        if (formData.emoteCount <= 2) {
          total = formData.emoteCount * convertCurrency(10000);
          details.push(
            `${translate("paypal.emoteFields.oneEmotePrice")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount <= 2) {
          total = formData.emoteCount * convertCurrency(10000);
          details.push(
            `${translate("paypal.emoteFields.oneEmotePrice")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount === 3) {
          total = convertCurrency(28500);
          details.push(
            `${translate("paypal.emoteFields.threeEmoteSet")}: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount <= 5) {
          total = formData.emoteCount * convertCurrency(10000);
          details.push(
            `${translate("paypal.emoteFields.oneEmotePrice")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount === 6) {
          total = convertCurrency(54000);
          details.push(
            `${translate("paypal.emoteFields.sixEmoteSet")}: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount >= 7) {
          total =
            convertCurrency(54000) +
            (formData.emoteCount - 6) * convertCurrency(9000);
          details.push(
            `${translate("paypal.emoteFields.moreEmoteSet")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        }
        break;

      case "illustrationEmote":
        if (formData.emoteCount <= 2) {
          total = formData.emoteCount * convertCurrency(5000);
          details.push(
            `${translate("paypal.emoteFields.oneEmotePrice02")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount === 3) {
          total = convertCurrency(14250);
          details.push(
            `${translate("paypal.emoteFields.threeEmoteSet")}: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount === 6) {
          total = convertCurrency(27000);
          details.push(
            `${translate("paypal.emoteFields.sixEmoteSet")}: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        } else if (formData.emoteCount >= 7) {
          total =
            convertCurrency(27000) +
            (formData.emoteCount - 6) * convertCurrency(4500);
          details.push(
            `${translate("paypal.emoteFields.moreEmoteSet02")} x ${
              formData.emoteCount
            }: ${translate(
              "paypal.moneyMark"
            )}${total.toLocaleString()} ${translate("paypal.money")}`
          );
        }
        break;
    }
    if (isBusiness) {
      let businessPrice = Math.round(total * 0.1021);
      details.push(
        `${translate(
          "paypal.business.businessFee"
        )}: ${businessPrice} ${translate("paypal.money")}`
      );
      total += businessPrice;
    }

    let fixedFee = convertCurrency(40); // 固定手数料（円）

    // 海外と国内の手数料
    if (isInternational) {
      let fee = Math.round(calculatePaypalFee(total)[0]);
      let exchangeFeeRate = Math.round(
        calculatePaypalFee(total, isInternational)[1]
      );
      details.push(
        `${translate("paypal.internationalFee")}+${translate(
          "paypal.moneyMark"
        )}${fixedFee}${translate("paypal.money")}: ${translate(
          "paypal.moneyMark"
        )}${fee + fixedFee} ${translate("paypal.money")}`
      );
      details.push(
        `${translate("paypal.exchangeFee")}: ${translate(
          "paypal.moneyMark"
        )}${exchangeFeeRate} ${translate("paypal.money")}`
      );
      total += fee + exchangeFeeRate + fixedFee;
    } else {
      let fee = Math.round(calculatePaypalFee(total));
      details.push(
        `${translate("paypal.domesticFee")}+${translate(
          "paypal.moneyMark"
        )}${fixedFee}${translate("paypal.money")}: ${translate(
          "paypal.moneyMark"
        )}${fee + fixedFee} ${translate("paypal.money")}`
      );

      total += fee + fixedFee;
    }

    return { total, details };
  };

  // メール送信処理
  const handleSendEmail = () => {
    setIsSending(true);

    fetch(
      "https://backend-worker.virtualquokka.workers.dev/api/sendToDiscord",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("メール送信に失敗しました。もう一度お試しください。");
        }

        return response.json();
      })
      .then((data) => {
        console.log("送信成功:", data);
        alert(translate("alert.success"));
        setShowModal(false);
        setSubmit(false);
      })
      .catch((error) => {
        console.error("送信エラー:", error);
        alert(translate("alert.error"));
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <FormField
          label={translate("activityName")}
          type="text"
          name="activityName"
          value={formData.activityName}
          onChange={handleChange}
          disabled={showModal}
        />

        <FormField
          label={translate("business")}
          type="checkbox"
          name="isBusiness"
          checked={isBusiness}
          onCheckboxChange={(e) => setIsBusiness(e.target.checked)}
          disabled={showModal}
        />

        <FormField
          label={translate("international")}
          type="checkbox"
          name="isInternational"
          checked={isInternational}
          onCheckboxChange={(e) => {
            setIsInternational(e.target.checked);
            formData.isInternational = e.target.checked;
          }}
          disabled={showModal}
        />

        {isBusiness && (
          <FormField
            label={translate("businessName")}
            type="text"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            disabled={showModal}
          />
        )}

        <FormField
          label={translate("email")}
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={showModal}
        />

        <FormField
          label={translate("activityLink")}
          type="text"
          name="activityLink"
          value={formData.activityLink}
          onChange={handleChange}
          disabled={showModal}
        />

        <FormField
          label={translate("request")}
          type="select"
          name="requestType"
          value={formData.requestType}
          onChange={handleChange}
          options={[
            { label: translate("requestType.select"), value: "" },
            { label: translate("requestType.animation"), value: "animation" },
            {
              label: translate("requestType.illustration"),
              value: "illustration",
            },
            {
              label: translate("requestType.animationEmote"),
              value: "animationEmote",
            },
            {
              label: translate("requestType.illustrationEmote"),
              value: "illustrationEmote",
            },
            {
              label: translate("requestType.others"),
              value: "others",
            },
          ]}
          disabled={showModal}
        />

        {/* アニメーション選択時のフォーム */}
        {formData.requestType === "animation" && (
          <>
            {renderCharacterType()}
            {renderReferences()}
            {renderDetails()}
          </>
        )}

        {/* イラスト選択時のフォーム */}
        {formData.requestType === "illustration" && (
          <>
            {renderCharacterType()}
            {renderBackgroundFields()}
            {renderReferences()}
            {renderDetails()}
          </>
        )}

        {/* エモートのアニメーション選択時のフォーム */}
        {formData.requestType === "animationEmote" && (
          <>
            {renderEmoteFields()}
            {renderReferences()}
            {renderDetails()}
          </>
        )}

        {/* イラストエモート選択時のフォーム */}
        {formData.requestType === "illustrationEmote" && (
          <>
            {renderEmoteFields()}
            {renderReferences()}
            {renderDetails()}
          </>
        )}

        {/* その他選択時のフォーム */}
        {formData.requestType === "others" && <>{renderDetails()}</>}

        <FormField
          label={translate("deliveryDate")}
          type="date"
          name="deliveryDate"
          value={formData.deliveryDate}
          min={new Date().toISOString().split("T")[0]} // 今日の日付をセット
          onChange={handleChange}
          disabled={showModal}
        />

        <FormField
          label={translate("contactMethod")}
          type="select"
          name="contactMethod"
          value={formData.contactMethod}
          onChange={handleChange}
          options={[
            { label: "Discord", value: "discord" },
            { label: "Email", value: "email" },
          ]}
          disabled={showModal}
        />

        <FormField
          label={
            <>
              <a
                href="#commission-tos"
                style={{ textDecoration: "underline", color: "blue" }}
              >
                {translate("agreeToTOS")}
              </a>
            </>
          }
          type="checkbox"
          name="agreeToTOS"
          checked={agreeToTOS}
          onCheckboxChange={() => setAgreeToTOS(!agreeToTOS)}
          disabled={showModal}
        />

        <button className="submitButton" type="submit" disabled={showModal}>
          {translate("submit")}
        </button>
      </form>

      {/* 確認モーダル */}
      {showModal && (
        <div className="modal">
          <div className="modalDetail">
            <h2 className="modalTitle">{translate("modal.title")}</h2>
            <p>
              <strong>{translate("modal.activityName")}:</strong>{" "}
              {formData.activityName}
            </p>
            {formData.businessName && (
              <p>
                <strong>{translate("modal.businessName")}:</strong>{" "}
                {formData.businessName}
              </p>
            )}
            <p>
              <strong>{translate("modal.email")}:</strong> {formData.email}
            </p>
            {formData.requestType && (
              <p>
                <strong>{translate("modal.requestType")}:</strong>{" "}
                {typeMap[formData.requestType]}
              </p>
            )}
            {(formData.requestType === "animation" ||
              formData.requestType === "illustration") && (
              <p>
                <strong>{translate("modal.characterType")}:</strong>{" "}
                {typeMap[formData.characterType]}
              </p>
            )}
            {(formData.requestType === "animationEmote" ||
              formData.requestType === "illustrationEmote") &&
              formData.emoteCount > 0 && (
                <p>
                  <strong>{translate("modal.emoteCount")}:</strong>{" "}
                  {formData.emoteCount}
                </p>
              )}
            <p>
              <strong>{translate("modal.requestDetails")}:</strong>{" "}
              {formData.details}
            </p>
            {formData.requestType !== "others" && (
              <>
                <p>
                  <strong>{translate("modal.estimate")}:</strong>
                </p>

                <ul>
                  {formData.estimatedDetails?.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                  <h4>
                    {translate("modal.total")}：{translate("paypal.moneyMark")}
                    {formData.estimatedCost.toLocaleString()}{" "}
                    {translate("paypal.money")}
                  </h4>
                </ul>
              </>
            )}
          </div>
          <div className="contactButton">
            <button
              onClick={() => {
                setShowModal(false);
                setSubmit(false);
              }}
              className="contactCancel"
            >
              {translate("modal.cancel")}
            </button>
            <button
              onClick={handleSendEmail}
              className="contactSend"
              disabled={isSending}
            >
              {isSending
                ? translate("modal.sending")
                : translate("modal.confirmSend")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactForm;
